// Small effects made of short-lived bits (Wunsch User 2026-10-19): sparks where bullets bounce,
// splinters where they hit, a shield flaring up, sparks when tanks ram, smoke from
// badly hurt tanks, embers along the zone's edge, a ring where a power-up is picked up (and for
// the one who took it its symbol rising). All from what the frames show; nothing for
// tanks the viewer cannot see. Cheap: plain dots and circles, a few hundred at most.

import { sound, soundOff } from './sound.js';
import { rumble } from './input.js';

const MAX_BITS = 400;

/** How a popup of points looks. */
export type Popup = 'kill' | 'bounty' | 'own' | 'challenge';
const SHIELD = 1; // power-up kind of the shield (`render.ts`)
const HIT_REACH = 30; // a bullet gone this close to a tank hit it
const LOW_HP = 0.34; // at about 1 HP of 3 a tank smokes
const SMOKE_EVERY_MS = 300;
const RISE_MS = 900; // a picked-up power-up's symbol rises this long (own tank only)
const FALL_MS = 650; // a negative power-up hitting an own tank falls onto it this long
const POPUP_MS = 1100; // a „+1“ over the own tank rises this long
const HURT_SHARE = 0.05; // an own tank losing more of its HP than this in a frame shakes the controller

interface Bit {
  x: number;
  y: number;
  vx: number;
  vy: number;
  start: number;
  life: number;
  size: number;
  color: string;
  kind: 'dot' | 'smoke' | 'ring';
  /** Rings: how much they widen over their life (share of `size`). */
  grow?: number;
}

/** What a frame shows, as far as the bits need it. */
export interface BitFrame {
  tanks: Float32Array;
  tankStride: number;
  bullets: Float32Array; // stride 5: id, x, y, flags, owner
  width: number;
  height: number;
  zone?: Float32Array;
  /** The viewer's own tanks (online one, in the local game the keyboard players'). */
  own: (i: number) => boolean;
  tankRadius: number;
  pickups?: Float32Array; // stride 3: x, y, kind
  pickupRadius: number;
  /** Explosions (stride `blastStride`: x, y, radius, age 0..1, shockwave), for their sound. */
  blasts?: Float32Array;
  blastStride: number;
}

export class Particles {
  private bits: Bit[] = [];
  /** Per bullet: last position and step. */
  private flying = new Map<number, { x: number; y: number; dx: number; dy: number }>();
  /** Per tank last frame: alive, shield up. */
  private was: { alive: boolean; shield: boolean }[] = [];
  /** Distance of each pair of tanks last frame (key i * 64 + j). */
  private apart = new Map<number, number>();
  private smoked: number[] = [];
  private lying: number[] = [];
  private popups: { tank: number; text: string; style: Popup; color: string; start: number }[] = [];
  /** The tanks of the last update, for where the popups are. */
  private tanks: Float32Array<ArrayBufferLike> = new Float32Array();
  private stride = 1;
  private risers: { x: number; y: number; kind: number; start: number }[] = [];
  /** Negative power-ups just hitting an own tank (affliction bit), falling onto it. */
  private fallers: { tank: number; bit: number; start: number }[] = [];
  /** Per tank last frame: its affliction bits. */
  private afflicted: number[] = [];
  /** Explosions heard lately (x, y, when), so each sounds once. */
  private heard: [number, number, number][] = [];
  /** Per tank last frame: the power-up it held (kind + 1), its poison time left (0..1). */
  private held: number[] = [];
  private poisoned: number[] = [];
  /** Per tank last frame: HP share and respawns, for the controller's rumble. */
  private health: [number, number][] = [];

  private add(bit: Omit<Bit, 'start'>, now: number): void {
    if (this.bits.length < MAX_BITS) this.bits.push({ ...bit, start: now });
  }

  /** `count` dots flying off from `x`, `y`, around `angle` (spread `spread`), `speed` per second. */
  private burst(x: number, y: number, count: number, colors: string[], now: number, angle = 0, spread = Math.PI * 2, speed = 90): void {
    for (let k = 0; k < count; k++) {
      const a = angle + (Math.random() - 0.5) * spread;
      const v = speed * (0.5 + Math.random() * 0.7);
      this.add({ x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, life: 160 + Math.random() * 140, size: 1.4 + Math.random(), color: colors[k % colors.length], kind: 'dot' }, now);
    }
  }

  /** `visible(i)`: tank `i` may be shown (alive, not hidden or cloaked, in sight); `color(i)` its colour. */
  update(f: BitFrame, visible: (i: number) => boolean, color: (i: number) => string, now: number): void {
    this.bits = this.bits.filter((b) => now - b.start < b.life);
    this.popups = this.popups.filter((p) => now - p.start < POPUP_MS);
    const { tanks, tankStride: s, bullets } = f;
    [this.tanks, this.stride] = [tanks, s];
    const n = tanks.length / s;
    const alive = (i: number) => tanks[i * s + 4] === 1;

    // Bullets: a sharp turn is a bounce, gone without a blast near a tank a hit.
    const seen = new Set<number>();
    for (let k = 0; k < bullets.length; k += 5) {
      const [id, x, y] = [bullets[k], bullets[k + 1], bullets[k + 2]];
      seen.add(id);
      const last = this.flying.get(id);
      const [dx, dy] = last ? [x - last.x, y - last.y] : [0, 0];
      if (last && Math.hypot(dx, dy) > 0.5 && Math.hypot(last.dx, last.dy) > 0.5) {
        const turn = (dx * last.dx + dy * last.dy) / (Math.hypot(dx, dy) * Math.hypot(last.dx, last.dy));
        if (turn < 0.5) {
          // At the border metal sparks, at a rock grey chips.
          const edge = last.x < 8 || last.y < 8 || last.x > f.width - 8 || last.y > f.height - 8;
          const colors = edge ? ['#fff3b0', '#ffd34d', '#ffffff'] : ['#bdbdb4', '#8a8a82', '#e0e0d8'];
          this.burst(last.x, last.y, 5, colors, now, Math.atan2(dy, dx), 1.6);
          sound('bounce', last.x, last.y);
        }
      }
      this.flying.set(id, { x, y, dx, dy });
    }
    const gone = [...this.flying.keys()].filter((id) => !seen.has(id));
    const cleared = bullets.length === 0 && gone.length > 3; // a new round
    for (const id of gone) {
      const { x, y } = this.flying.get(id)!;
      this.flying.delete(id);
      if (cleared) continue;
      let hit = -1;
      for (let i = 0; i < n; i++) {
        if (Math.hypot(tanks[i * s] - x, tanks[i * s + 1] - y) < HIT_REACH && (hit < 0 || Math.hypot(tanks[i * s] - x, tanks[i * s + 1] - y) < Math.hypot(tanks[hit * s] - x, tanks[hit * s + 1] - y))) hit = i;
      }
      if (hit < 0 || !(this.was[hit]?.alive ?? alive(hit)) || !visible(hit)) continue;
      if (this.was[hit]?.shield) {
        this.add({ x: tanks[hit * s], y: tanks[hit * s + 1], vx: 0, vy: 0, life: 260, size: f.tankRadius * 1.3, color: '#5ec8ff', kind: 'ring' }, now);
        sound('shieldBreak', x, y);
      } else {
        sound('hit', x, y);
        this.burst(x, y, 5, [color(hit), color(hit), '#ffffff'], now, Math.atan2(y - tanks[hit * s + 1], x - tanks[hit * s]), 2.2, 70);
        this.add({ x, y, vx: 0, vy: 0, life: 90, size: 5, color: '#fffbe6', kind: 'smoke' }, now);
      }
    }

    // Rams: two tanks that just met, closing in.
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const key = i * 64 + j;
        if (!alive(i) || !alive(j)) {
          this.apart.delete(key);
          continue;
        }
        const d = Math.hypot(tanks[i * s] - tanks[j * s], tanks[i * s + 1] - tanks[j * s + 1]);
        const before = this.apart.get(key);
        const touch = 2 * f.tankRadius + 3;
        if (before !== undefined && d < touch && before >= touch && before - d > 1 && visible(i) && visible(j)) {
          const [mx, my] = [(tanks[i * s] + tanks[j * s]) / 2, (tanks[i * s + 1] + tanks[j * s + 1]) / 2];
          this.burst(mx, my, 8, ['#fff3b0', '#ffb84d', '#ffffff'], now, 0, Math.PI * 2, 110);
          sound('ram', mx, my);
        }
        this.apart.set(key, d);
      }
    }

    // Smoke from badly hurt tanks; the state of each for next frame.
    for (let i = 0; i < n; i++) {
      const o = i * s;
      if (alive(i) && tanks[o + 3] <= LOW_HP && visible(i) && now - (this.smoked[i] ?? 0) > SMOKE_EVERY_MS) {
        this.smoked[i] = now;
        this.add({ x: tanks[o] + (Math.random() - 0.5) * 8, y: tanks[o + 1] + (Math.random() - 0.5) * 8, vx: (Math.random() - 0.5) * 8, vy: -14, life: 1200, size: 5, color: '#3a3a36', kind: 'smoke' }, now);
      }
      this.was[i] = { alive: alive(i), shield: tanks[o + 7] === SHIELD + 1 };
    }

    // An own tank hurt shakes its controller, dying (also respawning in one tick) harder; poison
    // ticks and drone shots are too small for it.
    for (let i = 0; i < n; i++) {
      const [hp, respawns] = [alive(i) ? tanks[i * s + 3] : 0, tanks[i * s + 20]];
      const last = this.health[i];
      this.health[i] = [hp, respawns];
      if (!last || !f.own(i) || soundOff()) continue;
      if ((last[0] > 0 && hp === 0) || respawns !== last[1]) rumble(i, true);
      else if (hp < last[0] - HURT_SHARE) rumble(i, false);
    }

    // Power-ups gone next to a tank were picked up: a plain ring for everybody (it gives nothing
    // away), the symbol rising for the one who took it.
    const pickups = f.pickups ?? new Float32Array();
    for (let k = 0; k < this.lying.length; k += 3) {
      const [x, y, kind] = [this.lying[k], this.lying[k + 1], this.lying[k + 2]];
      let still = false;
      for (let q = 0; q < pickups.length && !still; q += 3) still = Math.abs(pickups[q] - x) < 0.5 && Math.abs(pickups[q + 1] - y) < 0.5;
      if (still) continue;
      const reach = f.pickupRadius + f.tankRadius + 4;
      let taker = -1;
      for (let i = 0; i < n && taker < 0; i++) if (alive(i) && Math.hypot(tanks[i * s] - x, tanks[i * s + 1] - y) < reach) taker = i;
      if (taker < 0 || !visible(taker)) continue;
      this.add({ x, y, vx: 0, vy: 0, life: 350, size: f.pickupRadius, color: '#ffffff', kind: 'ring', grow: 1.2 }, now);
      if (f.own(taker)) {
        this.risers.push({ x: tanks[taker * s], y: tanks[taker * s + 1], kind, start: now });
        sound('pickup', x, y);
      }
    }
    this.lying = Array.from(pickups);
    this.risers = this.risers.filter((r) => now - r.start < RISE_MS);

    // A negative power-up hitting an own tank falls onto it, big from above (Wunsch User
    // 2026-10-21); the others' only show at their tank.
    for (let i = 0; i < n; i++) {
      const bits = alive(i) ? tanks[i * s + 8] : 0;
      const fresh = bits & ~(this.afflicted[i] ?? 0);
      this.afflicted[i] = bits;
      if (!f.own(i)) continue;
      for (let bit = 1; bit <= fresh; bit <<= 1) if (fresh & bit) this.fallers.push({ tank: i, bit, start: now });
      if (fresh) sound('afflicted');
    }

    // A poison hit: its time left starts anew (from 1), a hiss where it hit.
    for (let i = 0; i < n; i++) {
      const left = alive(i) ? tanks[i * s + 18] : 0;
      if (left > (this.poisoned[i] ?? 0) + 0.05 && visible(i)) sound('poison', tanks[i * s], tanks[i * s + 1]);
      this.poisoned[i] = left;
    }

    // Own power-up used: it was held and no longer is (not by dying).
    for (let i = 0; i < n; i++) {
      const held = alive(i) ? tanks[i * s + 6] : 0;
      if (f.own(i) && alive(i) && this.held[i] && !held) sound('use');
      this.held[i] = held;
    }

    // Explosions: each new one once (they stay in the frames a while, ageing).
    this.heard = this.heard.filter(([, , at]) => now - at < 600);
    const blasts = f.blasts ?? new Float32Array();
    for (let k = 0; k < blasts.length; k += f.blastStride) {
      const [x, y, age, shock] = [blasts[k], blasts[k + 1], blasts[k + 3], blasts[k + 4]];
      if (age > 0.5 || this.heard.some(([hx, hy]) => Math.abs(hx - x) < 2 && Math.abs(hy - y) < 2)) continue;
      this.heard.push([x, y, now]);
      sound(shock === 1 ? 'shockwave' : 'explosion', x, y);
    }
    this.fallers = this.fallers.filter((d) => now - d.start < FALL_MS && tanks[d.tank * s + 8] & d.bit);

    // Embers drifting in from the zone's edge while it is smaller than the map.
    const z = f.zone;
    if (z?.length === 4 && (z[2] - z[0] < f.width - 1 || z[3] - z[1] < f.height - 1)) {
      const [w, h] = [z[2] - z[0], z[3] - z[1]];
      for (let k = 0; k < 2 && this.bits.length < 250; k++) {
        const along = Math.random() * 2 * (w + h);
        const [x, y, nx, ny] =
          along < w ? [z[0] + along, z[1], 0, 1] : along < 2 * w ? [z[0] + along - w, z[3], 0, -1] : along < 2 * w + h ? [z[0], z[1] + along - 2 * w, 1, 0] : [z[2], z[1] + along - 2 * w - h, -1, 0];
        const v = 6 + Math.random() * 8;
        this.add({ x, y, vx: nx * v + (Math.random() - 0.5) * 4, vy: ny * v + (Math.random() - 0.5) * 4, life: 1600, size: 1.6 + Math.random(), color: Math.random() < 0.5 ? '#ff8a2a' : '#ffd27a', kind: 'dot' }, now);
      }
    }
  }

  /**
   * Points over tank `tank` rising, „+1“: `kill` in the colour of who was killed (`color`), `bounty`
   * sparkling gold, `own` (itself or a teammate killed) purple on black, `challenge` (the daily
   * challenge's reward) bigger, gold and sparkling.
   */
  popup(tank: number, text: string, style: Popup, color: string, now: number): void {
    // Several at once stack up a little.
    const start = now + 120 * this.popups.filter((p) => p.tank === tank && now - p.start < 120).length;
    this.popups.push({ tank, text, style, color, start });
  }

  /** The popups, over everything else. */
  drawPopups(ctx: CanvasRenderingContext2D, r: number, now: number): void {
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = 'bold 15px system-ui, sans-serif';
    ctx.lineJoin = 'round';
    for (const p of this.popups) {
      const t = (now - p.start) / POPUP_MS;
      if (t < 0) continue;
      const o = p.tank * this.stride;
      if (o + 1 >= this.tanks.length) continue;
      const [x, y] = [this.tanks[o] + r * 1.1, this.tanks[o + 1] - r * 1.4 - 22 * t];
      ctx.globalAlpha = t < 0.7 ? 1 : (1 - t) / 0.3;
      const gold = p.style === 'bounty' || p.style === 'challenge';
      const [fill, edge] = gold ? ['#ffd54a', '#6b4a00'] : p.style === 'own' ? ['#b25cff', '#000000'] : [p.color, '#111111'];
      ctx.font = p.style === 'challenge' ? 'bold 22px system-ui, sans-serif' : 'bold 15px system-ui, sans-serif';
      ctx.lineWidth = 3.5;
      ctx.strokeStyle = edge;
      ctx.strokeText(p.text, x, y);
      ctx.fillStyle = fill;
      ctx.fillText(p.text, x, y);
      if (gold) {
        // Sparkles round the gold.
        ctx.fillStyle = '#fff6c2';
        const [n, rx, ry] = p.style === 'challenge' ? [7, 30, 15] : [4, 13, 9];
        for (let k = 0; k < n; k++) {
          const a = k * 1.7 + now / 180;
          const blink = 0.5 + 0.5 * Math.sin(now / 90 + k * 2);
          const size = 1.5 + 1.5 * blink;
          ctx.fillRect(x + Math.cos(a) * rx - size / 2, y + Math.sin(a) * ry - size / 2, size, size);
        }
      }
    }
    ctx.restore();
  }

  /** Negative power-ups falling onto own tanks now: tank, affliction bit, 0..1 of the way down. */
  falling(now: number): { tank: number; bit: number; t: number }[] {
    return this.fallers.map((d) => ({ tank: d.tank, bit: d.bit, t: (now - d.start) / FALL_MS }));
  }

  /** Power-up symbols rising over the own tank now: where, which, how faded (0..1 of the way). */
  rising(now: number): { x: number; y: number; kind: number; t: number }[] {
    return this.risers.map((r) => {
      const t = (now - r.start) / RISE_MS;
      return { x: r.x, y: r.y - 20 - 30 * t, kind: r.kind, t };
    });
  }

  /** `seen(x, y)`: in sight (fog of war). */
  draw(ctx: CanvasRenderingContext2D, now: number, seen: (x: number, y: number) => boolean): void {
    ctx.save();
    for (const b of this.bits) {
      const t = (now - b.start) / b.life;
      const secs = (now - b.start) / 1000;
      // Dots slow down as they fly; smoke rises steadily.
      const drift = b.kind === 'dot' ? (1 - Math.exp(-6 * secs)) / 6 : secs;
      const [x, y] = [b.x + b.vx * drift, b.y + b.vy * drift];
      if (!seen(x, y)) continue;
      ctx.globalAlpha = 1 - t;
      if (b.kind === 'dot') {
        ctx.fillStyle = b.color;
        ctx.fillRect(x - b.size / 2, y - b.size / 2, b.size, b.size);
      } else if (b.kind === 'smoke') {
        ctx.globalAlpha = 0.45 * (1 - t);
        ctx.fillStyle = b.color;
        ctx.beginPath();
        ctx.arc(x, y, b.size * (1 + 2 * t), 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.strokeStyle = b.color;
        ctx.lineWidth = 3 * (1 - t);
        ctx.beginPath();
        ctx.arc(x, y, b.size * (1 + (b.grow ?? 0.25) * t), 0, Math.PI * 2);
        ctx.stroke();
      }
    }
    ctx.restore();
  }
}
